import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ArrowRight } from "lucide-react";
import { PRICING_CATEGORIES, PRICING_DISCLAIMER, PRICING_NOTE_STRUCTURE } from "../data/pricing";

export default function Pricing({ onOrder }) {
  const [active, setActive] = useState(PRICING_CATEGORIES[0].key);
  const current = PRICING_CATEGORIES.find((c) => c.key === active);

  return (
    <section
      id="cenik"
      data-testid="pricing-section"
      className="relative bg-slate-50 py-24 lg:py-32"
    >
      {/* faint icy/gold tinted top edge for separation from dark hero */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-amber-50 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <span className="text-xs uppercase tracking-[0.22em] text-blue-700 font-semibold">
            Ceník
          </span>
          <h2
            data-testid="pricing-title"
            className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight text-balance"
          >
            Orientační ceník služeb.
          </h2>
          <p
            data-testid="pricing-subtitle"
            className="font-body mt-6 text-lg text-slate-600 leading-relaxed"
          >
            Moderní online zajištění STK ve Znojmě bez zbytečného čekání a starostí.
            Ceny jsou orientační — výsledná cena se může lišit podle typu vozidla,
            paliva, kategorie, dokladů, rozsahu služby a aktuálního ceníku partnerské STK.
            Konečnou cenu vždy potvrzujeme předem.
          </p>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Kategorie ceníku"
          data-testid="pricing-tabs"
          className="mt-12 flex flex-wrap justify-center gap-2"
        >
          {PRICING_CATEGORIES.map((cat) => {
            const isActive = cat.key === active;
            return (
              <button
                key={cat.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(cat.key)}
                data-testid={`pricing-tab-${cat.key}`}
                className={[
                  "rounded-full px-5 py-2.5 text-sm font-semibold transition-all",
                  isActive
                    ? "bg-slate-900 text-amber-300 ring-1 ring-amber-300/40 tab-active"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-amber-300 hover:text-slate-900",
                ].join(" ")}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Category blurb */}
        <p className="mt-8 text-center text-slate-500 max-w-2xl mx-auto">
          {current.blurb}
        </p>

        {/* Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6"
            data-testid={`pricing-grid-${active}`}
          >
            {current.items.map((item) => (
              <PricingCard
                key={item.id}
                item={item}
                category={current.label}
                onOrder={onOrder}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Cena se skládá ze 3 částí */}
        <div
          data-testid="pricing-structure"
          className="mt-14 mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
        >
          <div className="font-heading text-lg font-bold text-slate-900">
            {PRICING_NOTE_STRUCTURE.title}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PRICING_NOTE_STRUCTURE.parts.map((p, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-4">
                <div className="text-sm font-bold text-slate-900">{p.name}</div>
                <div className="mt-2 text-xs text-slate-600 leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info box */}
        <div
          data-testid="pricing-info-box"
          className="mt-8 mx-auto max-w-3xl flex items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900"
        >
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-600/15 text-amber-700">
            <Info className="h-5 w-5" />
          </div>
          <div className="text-sm leading-relaxed">
            <strong className="font-semibold">Orientační ceny.</strong> {PRICING_DISCLAIMER}
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCard({ item, category, onOrder }) {
  return (
    <article
      data-testid={`pricing-card-${item.id}`}
      className="group relative flex flex-col rounded-3xl border border-slate-200 bg-white p-7 lg:p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(15,23,42,0.18)] hover:border-slate-300"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-heading text-xl lg:text-2xl font-bold text-slate-900 leading-tight">
          {item.name}
        </h3>
        <span className="shrink-0 rounded-full bg-amber-100 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-800">
          {category}
        </span>
      </div>

      <p className="mt-3 text-slate-600 text-sm leading-relaxed">
        {item.description}
      </p>

      <div className="my-6 h-px w-full bg-gradient-to-r from-amber-200/0 via-amber-300/60 to-amber-200/0" />

      <div className="flex items-end gap-3">
        <span
          className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight bg-gradient-to-b from-amber-600 to-amber-800 bg-clip-text text-transparent"
          data-testid={`pricing-price-${item.id}`}
        >
          {item.price}
        </span>
        {item.unit && (
          <span className="pb-2 text-sm text-slate-500">{item.unit}</span>
        )}
      </div>

      <button
        type="button"
        onClick={() => onOrder?.(item, category)}
        data-testid={`pricing-order-btn-${item.id}`}
        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 active:bg-black px-5 py-3.5 font-semibold text-amber-200 hover:text-amber-100 transition-colors"
      >
        Objednat službu
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
    </article>
  );
}
